import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { AvailabilityWindow } from "@/components/availability-picker";

function formatDate(yyyyMmDd: string): string {
  const [y, m, d] = yyyyMmDd.split("-").map((s) => Number(s));
  if (!y || !m || !d) return yyyyMmDd;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatMinute(minute: number): string {
  const hours = Math.floor(minute / 60) % 24;
  const mins = minute % 60;
  const dt = new Date();
  dt.setHours(hours, mins, 0, 0);
  return dt.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function AvailabilitySection({
  windows,
}: {
  windows: Array<AvailabilityWindow> | undefined;
}) {
  const { t } = useTranslation("profile");

  const sorted = useMemo(() => {
    return [...(windows ?? [])].sort((a, b) => a.date.localeCompare(b.date));
  }, [windows]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-6">
      <div className="text-lg font-semibold mb-3">
        {t("worker.availabilitySection")}
      </div>
      {sorted.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          {t("worker.noPostedAvailability")}
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {sorted.map((w, idx) => (
            <li
              key={`${w.date}-${idx}`}
              className="flex justify-between items-center text-sm border-b last:border-b-0 pb-2 last:pb-0"
            >
              <span className="font-medium">{formatDate(w.date)}</span>
              <span className="text-muted-foreground">
                {w.anyTime
                  ? t("worker.availabilityAnyTime")
                  : w.startMinute != null && w.endMinute != null
                    ? `${formatMinute(w.startMinute)} – ${formatMinute(w.endMinute)}`
                    : "—"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
